import React, { useEffect, useRef } from 'react';

export const useAppRouteEffects = (props) => {
  // Use a ref to track the last processed openUrl to prevent duplicate executions
  const lastProcessedOpenUrlRef = useRef(null);

  const {
    location,
    isProcessingUrl,
    setIsProcessingUrl,
    clearAllViews,
    SERVER_NAME,
    parseAndLoadPcapCsvData,
    setShowOpenPcapButton,
    setShowOpenFlowButton,
    setViewGraph,
    setShowBottom,
    setShowPanel,
    setIsDataReady,
    setSnackbarSeverity,
    setSnackbarMessage,
    setSnackbarOpen,
    loadNFTVisualization,
    setShowHomePage,
    setShowShortcutPage,
    setTokenView,
    setFilterToken,
    setFilterNFT,
    stopCameraSpinRef,
    setMyLikesFilter,
    setAllLikesFilter,
    setDefaultNodeId,
    setViewTransaction,
    setShowCustomNFTMarketplace,
    setShowE3DMarketplace,
    setShowCustomNFTDialog,
    setDataSource,
    ETHERSCAN,
    PCAP,
    setOrganicLayout,
    setUserTransactions,
    setHasPriceHistoryData,
    setRangeSelected,
    setSearchText,
    classifyGraphNodes,
    setGraphData,
    setTokenList,
    setGraphViewMode,
    setMinLinks,
    setIsLoading,
    setIsTourRunning,
    setDateRange2,
    setShowNewsletter,
    setNewsDate,
    dateRange2,
    setFilterNewOnly,
    setFilterNFTOnly,
    createTokenList,
    layoutManagerRef,
    getTokenListWithHistoryAllRanges,
    axios,
    genericTokenIcon,
    setMaxNodes,
    setSpinSpeed,
    setSpinCamera,
    setIsConfigured,
    setProgress,
    setProgressVisible,
  } = props || {};

  useEffect(() => {
    console.log(`App.js: Handle URL parameters: ${location.pathname}${location.search}`);

    if (isProcessingUrl) {
      console.log('Currently processing URL, skipping this run');
      return;
    }

    setIsProcessingUrl(true);

    const pathParts = location.pathname.split('/');
    const searchParams = new URLSearchParams(location.search);

    try {
      // Don't clear views if we're loading a PCAP from URL - the async handler will manage state
      const isPcapWithOpenUrl = pathParts[1] === 'pcap' && (searchParams.get('openUrl') || searchParams.get('openurl'));
      if (!isPcapWithOpenUrl) {
        clearAllViews();
      }

      try {
        if (typeof setShowOpenFlowButton === 'function') setShowOpenFlowButton(false);
      } catch (e) {
      }

      if (pathParts[1] === 'pcap') {
        const openUrlParam = searchParams.get('openUrl') || searchParams.get('openurl');
        if (openUrlParam) {
          const raw = String(openUrlParam || '').trim();
          if (!raw) return;

          // Skip if we've already processed this exact URL
          if (lastProcessedOpenUrlRef.current === raw) {
            try { console.log('[PCAP][gallery] already processed this URL, skipping', { raw }); } catch (e) {}
            return;
          }
          lastProcessedOpenUrlRef.current = raw;

          (async () => {
            try {
              try {
                console.log('[PCAP][gallery] /pcap?openUrl handler start', { raw });
              } catch (e) {}

              // Hide overlay buttons immediately to prevent flashing
              try { if (typeof setShowOpenPcapButton === 'function') setShowOpenPcapButton(false); } catch (e) {}
              try { if (typeof setIsLoading === 'function') setIsLoading(true); } catch (e) {}
              try { if (typeof setDataSource === 'function') setDataSource(PCAP); } catch (e) {}
              try { if (typeof setViewGraph === 'function') setViewGraph(true); } catch (e) {}

              try {
                const startedAtHook = Date.now();
                while (true) {
                  const hook = (typeof window !== 'undefined') ? window.__E3D_OPEN_PCAP_URL_VIA_JOB__ : null;
                  if (typeof hook === 'function') {
                    try { console.log('[PCAP][gallery] using window.__E3D_OPEN_PCAP_URL_VIA_JOB__'); } catch (e) {}
                    await hook(raw);
                    try { setDataSource(PCAP); } catch (e) {}
                    try { setOrganicLayout(true); } catch (e) {}
                    try { setViewGraph(true); } catch (e) {}
                    try {
                      let pcapShowBottomPref = false;
                      try {
                        const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
                        if (v != null) pcapShowBottomPref = String(v) === 'true';
                      } catch (e2) {
                      }
                      setShowBottom(!!pcapShowBottomPref);
                    } catch (e) {}
                    try { setShowPanel(true); } catch (e) {}
                    try { setIsDataReady(true); } catch (e) {}
                    try { console.log('[PCAP][gallery] window hook completed'); } catch (e) {}
                    return;
                  }
                  if ((Date.now() - startedAtHook) > 5000) break;
                  await new Promise((r) => setTimeout(r, 150));
                }
              } catch (e) {
                try { console.error('[PCAP][gallery] hook wait error:', e); } catch (e2) {}
              }

              try { console.log('[PCAP][gallery] using fallback handler'); } catch (e) {}
              try { if (typeof setProgressVisible === 'function') setProgressVisible(true); } catch (e) {}
              try { if (typeof setProgress === 'function') setProgress(0); } catch (e) {}

              const createResp = await axios.post(`${SERVER_NAME}/openPcapUrlJob`, { url: raw }, { withCredentials: false });
              const jobId = createResp && createResp.data && createResp.data.jobId ? String(createResp.data.jobId) : '';
              if (!jobId) throw new Error('Failed to start job');

              const startedAt = Date.now();
              const maxWaitMs = 30 * 60 * 1000;
              let lastProgress = 0;

              while (true) {
                if ((Date.now() - startedAt) > maxWaitMs) throw new Error('Timed out waiting for job');

                const statusResp = await axios.get(`${SERVER_NAME}/openPcapUrlJob/${encodeURIComponent(jobId)}/status`, { withCredentials: false });
                const phase = statusResp && statusResp.data && statusResp.data.phase ? String(statusResp.data.phase) : '';
                const errMsg = statusResp && statusResp.data && statusResp.data.error ? String(statusResp.data.error) : '';
                const percent = statusResp && statusResp.data && statusResp.data.download && statusResp.data.download.percent != null
                  ? Number(statusResp.data.download.percent)
                  : null;

                if (phase === 'error') throw new Error(errMsg || 'Job failed');
                if (phase === 'done') {
                  try { if (typeof setProgress === 'function') setProgress(100); } catch (e) {}
                  break;
                }

                let p = lastProgress;
                if (phase === 'downloading') {
                  if (percent != null && Number.isFinite(percent)) {
                    p = Math.max(p, Math.min(80, Math.round((percent / 100) * 80)));
                  } else {
                    p = Math.max(p, 5);
                  }
                } else if (phase === 'converting') {
                  p = Math.max(p, 90);
                } else {
                  p = Math.max(p, 1);
                }
                lastProgress = p;
                try { if (typeof setProgress === 'function') setProgress(p); } catch (e) {}

                await new Promise((r) => setTimeout(r, 1000));
              }

              const resultResp = await axios.get(`${SERVER_NAME}/openPcapUrlJob/${encodeURIComponent(jobId)}/result`, { withCredentials: false, responseType: 'text' });
              const csvText = resultResp && typeof resultResp.data === 'string' ? resultResp.data : '';
              if (!csvText) throw new Error('Failed to open URL: empty response');

              await parseAndLoadPcapCsvData(csvText);
              try { setDataSource(PCAP); } catch (e) {}
              try { setOrganicLayout(true); } catch (e) {}
              try { setShowOpenPcapButton(false); } catch (e) {}
              try { setViewGraph(true); } catch (e) {}
              try {
                let pcapShowBottomPref = false;
                try {
                  const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
                  if (v != null) pcapShowBottomPref = String(v) === 'true';
                } catch (e2) {
                }
                setShowBottom(!!pcapShowBottomPref);
              } catch (e) {}
              try { setShowPanel(true); } catch (e) {}
              try { setIsDataReady(true); } catch (e) {}
            } catch (e) {
              try {
                try { console.error('[PCAP][gallery] /pcap?openUrl load failed:', e); } catch (e3) {}
                setSnackbarSeverity('error');
                setSnackbarMessage(`Failed to load PCAP URL: ${e && e.message ? e.message : 'Unknown error'}`);
                setSnackbarOpen(true);
              } catch (e2) {
              }
            } finally {
              try { if (typeof setProgressVisible === 'function') setProgressVisible(false); } catch (e) {}
              try { if (typeof setIsLoading === 'function') setIsLoading(false); } catch (e) {}
              try { if (typeof setIsProcessingUrl === 'function') setIsProcessingUrl(false); } catch (e) {}
            }
          })();
        }

        const pcapParam = searchParams.get('pcap');
        if (pcapParam) {
          (async () => {
            try {
              const raw = String(pcapParam);
              const pcapUrl = raw;
              try {
                console.log('[PCAP][share] /pcap?pcap handler start', { raw });
              } catch (e) {}

              const tryFetchText = async (u) => {
                const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                const r = await fetch(u, { credentials: 'omit' });
                if (!r || !r.ok) return { ok: false, status: r ? r.status : null, text: '' };
                const t = await r.text();
                const head = String(t || '').slice(0, 240).toLowerCase();
                const isHtml = head.includes('<!doctype html') || head.includes('<html');
                try {
                  const t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                  console.log('[PCAP][share] fetch result', {
                    url: u,
                    status: r.status,
                    ms: Math.round(t1 - t0),
                    bytes: String(t || '').length,
                    isHtml,
                    preview: String(t || '').slice(0, 120),
                  });
                } catch (e) {}
                return { ok: !isHtml, status: r.status, text: t, isHtml };
              };

              const qs = `url=${encodeURIComponent(pcapUrl)}`;
              let fetched = null;

              const candidates = [
                `${SERVER_NAME}/pcapToCsv?${qs}`,
                `${SERVER_NAME}/pcapToCsv?${qs}`,
              ];

              let lastErr = null;
              for (const c of candidates) {
                try {
                  fetched = await tryFetchText(c);
                  if (fetched && fetched.ok) break;
                } catch (e) {
                  lastErr = e;
                }
              }

              if (!fetched || !fetched.ok) {
                throw lastErr || new Error(`Failed to convert PCAP (${(fetched && fetched.status) || 'network'})`);
              }

              const text = fetched.text;
              try {
                console.log('[PCAP][share] starting parseAndLoadPcapCsvData (from pcap)', { bytes: String(text || '').length });
              } catch (e) {}
              await parseAndLoadPcapCsvData(text);
              try {
                console.log('[PCAP][share] parseAndLoadPcapCsvData done (from pcap)');
              } catch (e) {}
              try { setShowOpenPcapButton(false); } catch (e) {}
              try { setViewGraph(true); } catch (e) {}
              try {
                let pcapShowBottomPref = false;
                try {
                  const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
                  if (v != null) pcapShowBottomPref = String(v) === 'true';
                } catch (e2) {
                }
                setShowBottom(!!pcapShowBottomPref);
              } catch (e) {}
              try { setShowPanel(true); } catch (e) {}
              try { setIsDataReady(true); } catch (e) {}
            } catch (e) {
              try {
                try { console.error('[PCAP][share] /pcap?pcap load failed:', e); } catch (e3) {}
                setSnackbarSeverity('error');
                setSnackbarMessage(`Failed to load shared PCAP: ${e && e.message ? e.message : 'Unknown error'}`);
                setSnackbarOpen(true);
              } catch (e2) {
              }
            }
          })();
        }
        const csvParam = searchParams.get('csv');
        if (csvParam) {
          (async () => {
            try {
              const raw = String(csvParam);
              const url = raw.startsWith('ipfs://')
                ? raw.replace('ipfs://', 'https://ipfs.io/ipfs/')
                : raw;
              try {
                console.log('[PCAP][share] /pcap?csv handler start', { raw, url });
              } catch (e) {}
              const tryFetchText = async (u) => {
                const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                const r = await fetch(u, { credentials: 'omit' });
                if (!r || !r.ok) return { ok: false, status: r ? r.status : null, text: '' };
                const t = await r.text();
                const head = String(t || '').slice(0, 240).toLowerCase();
                const isHtml = head.includes('<!doctype html') || head.includes('<html');
                try {
                  const t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                  console.log('[PCAP][share] fetch result', {
                    url: u,
                    status: r.status,
                    ms: Math.round(t1 - t0),
                    bytes: String(t || '').length,
                    isHtml,
                    preview: String(t || '').slice(0, 120),
                  });
                } catch (e) {}
                return { ok: !isHtml, status: r.status, text: t, isHtml };
              };

              let fetched = await tryFetchText(url);
              if (!fetched.ok) {
                const proxyUrl = `${SERVER_NAME}/fetchUrl?url=${encodeURIComponent(url)}`;
                try {
                  console.log('[PCAP][share] falling back to proxy', { proxyUrl });
                } catch (e) {}
                fetched = await tryFetchText(proxyUrl);
              }

              if (!fetched.ok) {
                try {
                  console.warn('[PCAP][share] failed to fetch CSV after proxy fallback', { url, status: fetched.status });
                } catch (e) {}
                throw new Error(`Failed to fetch CSV (${fetched.status || 'network'})`);
              }

              const text = fetched.text;
              try {
                console.log('[PCAP][share] starting parseAndLoadPcapCsvData', { bytes: String(text || '').length });
              } catch (e) {}
              await parseAndLoadPcapCsvData(text);
              try {
                console.log('[PCAP][share] parseAndLoadPcapCsvData done');
              } catch (e) {}
              try { setShowOpenPcapButton(false); } catch (e) {}
              try { setViewGraph(true); } catch (e) {}
              try {
                let pcapShowBottomPref = false;
                try {
                  const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
                  if (v != null) pcapShowBottomPref = String(v) === 'true';
                } catch (e2) {
                }
                setShowBottom(!!pcapShowBottomPref);
              } catch (e) {}
              try { setShowPanel(true); } catch (e) {}
              try { setIsDataReady(true); } catch (e) {}
            } catch (e) {
              try {
                try { console.error('[PCAP][share] /pcap?csv load failed:', e); } catch (e3) {}
                setSnackbarSeverity('error');
                setSnackbarMessage(`Failed to load shared CSV: ${e && e.message ? e.message : 'Unknown error'}`);
                setSnackbarOpen(true);
              } catch (e2) {
              }
            }
          })();
        }
      }

      if (pathParts[1] != 'token') {
        const nftParam = searchParams.get('nft');
        if (pathParts[1] === 'nft' && nftParam) {
          loadNFTVisualization(nftParam);
        }
        else if (searchParams.has('nft')) {
          loadNFTVisualization(searchParams.get('nft'));
        }
      }

      if (pathParts[1] === 'home' || pathParts[1] === '') {
        setShowHomePage(true);
        setShowShortcutPage(true);
        // When landing on the home route, default the token view to Chain
        setTokenView('chain');
      }
    else if (pathParts[1] === 'price') {
      setViewGraph(false);
      setShowHomePage(false);
      setShowBottom(false);
      setFilterToken(true);

      if (pathParts[2]) {
        const address = pathParts[2];

        if (address === 'mylikes') {
          setMyLikesFilter(true);
        }
        else
        if (address === 'alllikes') {
          setAllLikesFilter(true);
        }
        else {
          const isAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
          if (isAddress) {
            setDefaultNodeId(address);
          }
        }
      }
    }
    else
    if (pathParts[1] === 'rwa') {
      setShowCustomNFTMarketplace(true);
      setShowE3DMarketplace(false);
      setShowHomePage(true);
      setShowShortcutPage(false);
      setViewGraph(false);
    }
    else
    if (pathParts[1] === 'customnft') {
      setShowCustomNFTDialog(true);
      setShowHomePage(true);
      setShowShortcutPage(false);
      setViewGraph(false);
    }
    else
    if (pathParts[1] === 'nft') {
      setFilterToken(false);
      setFilterNFT(true);
      setViewGraph(false);
      setShowBottom(false);
      try {
        const fn = stopCameraSpinRef && stopCameraSpinRef.current;
        if (typeof fn === 'function') fn();
      } catch (e) {
      }

      if (pathParts[2]) {
        const address = pathParts[2];

        if (address === 'mylikes') {
          setMyLikesFilter(true);
        }
        else
        if (address === 'alllikes') {
          setAllLikesFilter(true);
        }
        else {
          const isAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
          if (isAddress) {
            setDefaultNodeId(address);
          }
        }
      }
    }
    else
    if (pathParts[1] === 'links' || pathParts[1] === 'graphs') {
      window.prefetchedForensicsWasUsed = false;

      const snapshotUrl = searchParams.get('snapshot');

      if (!snapshotUrl) {
        try {
          if (typeof window !== 'undefined' && window.__E3D_ACTIVE_SNAPSHOT_URL__) {
            window.__E3D_ACTIVE_SNAPSHOT_URL__ = null;
          }
        } catch (e) {
        }
      }

      setTokenView('links');
      setShowHomePage(false);
      setShowShortcutPage(false);

      setViewGraph(true);
      setFilterToken(true);
      setFilterNFT(false);

      setDataSource(ETHERSCAN);
      setShowOpenPcapButton(false);

      setOrganicLayout(true);
      setUserTransactions(true);
      setHasPriceHistoryData(false);
      setIsDataReady(false);

      if (pathParts[2] && /^0x[a-f0-9]{40}$/.test(pathParts[2])) {
        setShowPanel(false);
      } else {
        setShowPanel(true);
      }

      setRangeSelected('1M');

      if (snapshotUrl) {
        (async () => {
          try {
            const raw = String(snapshotUrl || '').trim();
            if (!raw) throw new Error('Empty snapshot url');

            const resp = await fetch(raw, { credentials: 'omit' });
            if (!resp || !resp.ok) {
              throw new Error(`Snapshot fetch failed (status=${resp ? resp.status : 'n/a'})`);
            }
            const snap = await resp.json();
            const gd = snap && snap.graphData ? snap.graphData : null;
            const nodes = gd && Array.isArray(gd.nodes) ? gd.nodes : [];
            const links = gd && Array.isArray(gd.links) ? gd.links : [];

            const normalizedLinks = links.map((l) => {
              if (!l) return l;
              const src = typeof l.source === 'object' ? l.source.id : l.source;
              const tgt = typeof l.target === 'object' ? l.target.id : l.target;
              return { ...l, source: src, target: tgt };
            });

            const normalizedGraph = classifyGraphNodes({
              nodes,
              links: normalizedLinks,
            });

            try {
              if (typeof window !== 'undefined') {
                window.__E3D_ACTIVE_SNAPSHOT_URL__ = raw;
              }
            } catch (e) {
            }

            setGraphData(normalizedGraph);
            setTokenList([]);
            setGraphViewMode('all');
            setMinLinks(1);
            setIsDataReady(true);
            setIsLoading(false);

            setViewGraph(true);
            setShowPanel(true);
          } catch (e) {
            try {
              setSnackbarSeverity('error');
              setSnackbarMessage(`Failed to load snapshot: ${e && e.message ? e.message : 'Unknown error'}`);
              setSnackbarOpen(true);
            } catch (e2) {
            }
            try {
              setIsDataReady(true);
              setIsLoading(false);
            } catch (e3) {
            }
          }
        })();
        return;
      }

      if (pathParts[2]) {
        const address = pathParts[2];

        const isAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
        if (isAddress) {
          setDefaultNodeId(address);
          setSearchText(address);

          if (typeof window !== 'undefined' && window.prefetchedForensics) {
            const snap = window.prefetchedForensics;
            if (snap && snap.token && snap.token.toLowerCase() === address.toLowerCase()) {
              try {
                const gd = snap.graphData || { nodes: [], links: [] };
                const tl = snap.tokenList || [];
                const normalizedLinks = Array.isArray(gd.links)
                  ? gd.links.map((l) => {
                      if (!l) return l;
                      const src = typeof l.source === 'object' ? l.source.id : l.source;
                      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
                      return { ...l, source: src, target: tgt };
                    })
                  : [];
                const normalizedGraph = classifyGraphNodes({
                  nodes: Array.isArray(gd.nodes) ? gd.nodes : [],
                  links: normalizedLinks,
                });
                setGraphData(normalizedGraph);
                setTokenList(tl);
                setGraphViewMode('all');
                setMinLinks(1);
                setIsDataReady(true);
                setIsLoading(false);

                setViewGraph(true);
                setShowPanel(true);
                window.prefetchedForensicsWasUsed = true;
              } catch (e) {
                console.error('Error using prefetched forensics snapshot:', e);
              } finally {
                window.prefetchedForensics = null;
              }
            }
          }
        }
      }
    }
    else
    if (pathParts[1] === 'token') {
      setTokenView('chain');
      setViewGraph(true);
      setFilterToken(true);
      setFilterNFT(false);
      setShowHomePage(false);
      setShowShortcutPage(false);
      setShowBottom(false);
      setDataSource(ETHERSCAN);
      setShowOpenPcapButton(false);

      const isOrganic = searchParams.get('organic') === 'true';
      const isUsers = searchParams.get('users') === 'true';
      const categoryFilter = searchParams.get('category');
      const timeWindow = searchParams.get('time_window');
      const isNew = searchParams.get('new') === 'true';
      const isNft = searchParams.get('nft') === 'true';

      setFilterNewOnly(isNew);
      setFilterNFTOnly(isNft);
      setTokenView(isNew ? 'links' : 'chain');

      if (isOrganic && isUsers) {
        setOrganicLayout(true);
        setUserTransactions(true);
        setHasPriceHistoryData(false);
        setIsDataReady(false);
        setRangeSelected('10M');
      } else {
        setOrganicLayout(false);
        setUserTransactions(false);
        setIsDataReady(false);
        setHasPriceHistoryData(false);

        if (categoryFilter && (!timeWindow || timeWindow === 'last_hour')) {
          let usedPrefetch = false;
          if (typeof window !== 'undefined' && Array.isArray(window.prefetchedCategoryTokens)) {
            const prefetched = window.prefetchedCategoryTokens;
            try {
              const tokens = createTokenList(prefetched, 'Token', ETHERSCAN);
              setTokenList(tokens);

              const metaNodes = tokens.map(t => ({
                id: t.id,
                label: t.label || t.name || (t.id && t.id.substring(0, 8)) || '',
                symbol: t.symbol || '',
                icon: t.icon || t.icon2 || genericTokenIcon,
                type: 'token',
              }));
              const newGraphData = { nodes: metaNodes, links: [] };
              setGraphData(newGraphData);
              try {
                const lm = layoutManagerRef && layoutManagerRef.current;
                if (lm && typeof lm.handleDistributeAll === 'function') lm.handleDistributeAll(newGraphData);
              } catch (e) {
              }

              setIsDataReady(true);
              usedPrefetch = true;
            } catch (e) {
              console.error('Error using prefetched category tokens:', e);
            } finally {
              window.prefetchedCategoryTokens = null;
            }
          }

          if (!usedPrefetch) {
            (async () => {
              try {
                const resp = await axios.get(SERVER_NAME + '/api/categoryTokensLastHour', {
                  params: { category: categoryFilter },
                });
                const rawTokens = resp.data || [];
                const tokens = createTokenList(rawTokens, 'Token', ETHERSCAN);
                setTokenList(tokens);

                const metaNodes = tokens.map(t => ({
                  id: t.id,
                  label: t.label || t.name || (t.id && t.id.substring(0, 8)) || '',
                  symbol: t.symbol || '',
                  icon: t.icon || t.icon2 || genericTokenIcon,
                  type: 'token',
                }));
                const newGraphData = { nodes: metaNodes, links: [] };
                setGraphData(newGraphData);
                try {
                  const lm = layoutManagerRef && layoutManagerRef.current;
                  if (lm && typeof lm.handleDistributeAll === 'function') lm.handleDistributeAll(newGraphData);
                } catch (e) {
                }

                setIsDataReady(true);
              } catch (error) {
                console.error('Error loading category tokens for /token view:', error);
                setIsDataReady(true);
              }
            })();
          }
        } else {
          getTokenListWithHistoryAllRanges(ETHERSCAN)
            .then((tokens) => {
              if (tokens && tokens.length > 0) {
                setTokenList(tokens);
                setHasPriceHistoryData(true);
              } else {
                setHasPriceHistoryData(false);
              }
              setIsDataReady(true);
            })
            .catch((error) => {
              console.error('Error loading meta price history for /token view:', error);
              setIsDataReady(true);
              setHasPriceHistoryData(false);
            });
        }
      }
    }
    else
    if (pathParts[1] === 'pcap') {
      setViewGraph(true);
      setFilterToken(true);
      setFilterNFT(false);
      setShowHomePage(false);
      try {
        let pcapShowBottomPref = false;
        try {
          const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
          if (v != null) pcapShowBottomPref = String(v) === 'true';
        } catch (e) {
        }
        setShowBottom(!!pcapShowBottomPref);
      } catch (e) {
        setShowBottom(false);
      }
      setShowPanel(true);
      setShowShortcutPage(false);
      setDataSource(PCAP);
      setOrganicLayout(true);
      const csvParam = searchParams.get('csv');
      const openUrlParam = searchParams.get('openUrl') || searchParams.get('openurl');
      setShowOpenPcapButton(!csvParam && !openUrlParam);
    }
    else
    if (pathParts[1] === 'flows') {
      setViewGraph(true);
      setFilterToken(true);
      setFilterNFT(false);
      setShowHomePage(false);
      try {
        let pcapShowBottomPref = false;
        try {
          const v = window && window.localStorage ? window.localStorage.getItem('pcapShowBottom') : null;
          if (v != null) pcapShowBottomPref = String(v) === 'true';
        } catch (e) {
        }
        setShowBottom(!!pcapShowBottomPref);
      } catch (e) {
        setShowBottom(false);
      }
      setShowPanel(true);
      setShowShortcutPage(false);
      setDataSource(PCAP);
      setOrganicLayout(true);
      setShowOpenPcapButton(false);
      try {
        if (typeof setShowOpenFlowButton === 'function') setShowOpenFlowButton(true);
      } catch (e) {
      }
    }
    else
    // This is the token view, but with no graph, and showBottom true
    if (pathParts[1] === 'transaction') {
      setViewTransaction(true);
      setFilterToken(true); 
      setShowBottom(true);
      setShowPanel(true);
      setFilterNFT(false);
      setViewGraph(false);

      // Check if there is a part after 'transaction'
      if (pathParts[2]) {
        const address = pathParts[2];
        
        if (address === 'mylikes') {
          setMyLikesFilter(true);
        }
        else
        if (address === 'alllikes') {
          setAllLikesFilter(true);
        }
        else {
          // Check if the address is in the correct form
          const isAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
          if (isAddress) {
            setDefaultNodeId(address);
          }
        }
      }
    }

    if (searchParams.has('tour')) {
      setTimeout(() => setIsTourRunning(true), 5000);
      setShowShortcutPage(false);
    }

    if (searchParams.has('hours')) {
      const hours = parseInt(searchParams.get('hours'), 10);
      if (!isNaN(hours)) {
        const endTimeUTC = new Date();
        const startTimeUTC = new Date(endTimeUTC.getTime() - hours * 60 * 60 * 1000);

        setDateRange2({
          startDate: startTimeUTC,
          endDate: endTimeUTC
        });
      }
    }

    if (searchParams.has('news')) {
      setShowNewsletter(true);
      setShowShortcutPage(false);

      if (searchParams.has('date')) {
        const newsDate = searchParams.get('newsDate');
        console.log('News Date:', newsDate);
        setNewsDate(newsDate);
      }
    }

    if (pathParts[1] === 'news') {
      setShowNewsletter(true);
      setShowShortcutPage(false);

      if (pathParts[2]) {
        const newsDate = pathParts[2];
        console.log('News Date:', newsDate);
        setNewsDate(newsDate);

        const [datePart, timePart] = newsDate.split('_');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split('-').map(Number);

        const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds);

        const startDate = new Date(parsedDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(parsedDate);
        endDate.setHours(23, 59, 59, 999);

        console.log('Start Date:', startDate);
        console.log('End Date:', endDate);
        const startTime = Math.floor(startDate.getTime() / 1000);
        const endTime = Math.floor(endDate.getTime() / 1000);
        setDateRange2({startDate: startTime, endDate: endTime});
        console.log('Date Range:', dateRange2);
      }
    }

    if (searchParams.has('users')) {
      setUserTransactions(true);
      setShowHomePage(false);
    }

   if (searchParams.has('organic')) {
     setOrganicLayout(true);
     setShowHomePage(false);
   }

   if (searchParams.has('new')) {
    setFilterNewOnly(true);
    setShowHomePage(false);
  }

    if (searchParams.has('maxNodes')) {
      const maxNodesValue = parseInt(searchParams.get('maxNodes'), 10);
      if (!isNaN(maxNodesValue)) {
        setMaxNodes(maxNodesValue);
        setShowHomePage(false);
      }
    }

    if (searchParams.has('nospin')) {
      setSpinSpeed(0);
      setSpinCamera(false);
      setShowHomePage(false);
    }

    if (searchParams.has('nopanel')) {
      setShowPanel(false);
      setShowHomePage(false);
    }

    setIsConfigured(true);
  } finally {
    // Don't reset isProcessingUrl for async pcap openUrl handler - it will reset itself
    if (!(pathParts[1] === 'pcap' && (searchParams.get('openUrl') || searchParams.get('openurl')))) {
      try { setIsProcessingUrl(false); } catch (e) {}
    }
  }
}, [location.pathname, location.search]);
};
